import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { userAtom, notificationsAtom } from '../../../atoms/Atoms';
import { H2, H4 } from '../../shared-components/typography/Title';
import AuthLayout from '../../shared-components/auth-layout/AuthLayout';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import api from '../../../api/api';
import { encryptString } from '../../../functions';
import envConfig from '../../../envConfig';

function Reset() {
    const [notifications, setNotifications] = useRecoilState(notificationsAtom);
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const user = useRecoilValue(userAtom);
    const [loading, setLoading] = useState(false);

    if (user) {
      window.location = '/home';
    }

    useEffect(() => {
      if (params) {
        api
          .post('oauth/reset/validation', {
            Id: params.id,
            Token: params.token,
          })
          .then((response) => {
            if (response.data.error) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: response.data.error,
                  error: true,
                },
              ]);
              navigate('/');
            } else if (!response.data.data) {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: response.data.message,
                  error: true,
                },
              ]);
              navigate('/');
            }
          })
          .catch((error) => {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: error.message,
                error: true,
              },
            ]);
            navigate('/');
          });
      } else {
        navigate('/');
      }
    }, [params]);

    function submit(values) {
      setLoading(true);
      api.post('auth/reset', {
        Id: params.id,
        Token: params.token,
        Password: encryptString(values.password),
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
          navigate('/');
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
    }

    const formik = useFormik({
      initialValues: {
        password: '',
        passwordConfirmation: ''
      },
      validationSchema: Yup.object().shape({
        password: Yup.string().required(t('required_field')),
        passwordConfirmation: Yup.string()
        .oneOf(
          [Yup.ref('password'), null],
          t('same_password')
        )
        .required(t('required_field')),
      }),
      onSubmit: (values, { setSubmitting }) => submit(values),
    });

    return (
      <AuthLayout title={`${t('welcome_message')} - ${envConfig.SiteTitle}`}>
          <H2 className="text-center" text={t('enter_your_new_password_below')} />
          <Form className="mt-6 w-full space-y-7">
            <Input
              label={t('password')}
              required
              id="password"
              name="password"
              type="password"
              className=""
              autoComplete="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={
                formik.errors.password &&
                formik.touched.password &&
                formik.errors.password
              }
            />
            <Input
              label={t('confirm_password')}
              required
              id="passwordConfirmation"
              name="passwordConfirmation"
              type="password"
              autoComplete="password"
              value={formik.values.passwordConfirmation}
              onChange={formik.handleChange}
              error={
                formik.errors.passwordConfirmation &&
                formik.touched.passwordConfirmation &&
                formik.errors.passwordConfirmation
              }
            />
          </Form>
          <Button
            className="bg-primaryColor mt-8 mb-3 w-full"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('submit')}
            onClick={formik.submitForm}
          />
          <H4 className="mb-3 text-center text-muted" text={t('or')} />
          <Button
            disabled={loading}
            className="bg-transparent shadow-none hover:shadow-none mb-3 w-full"
            textColor="text-secondaryTextColor"
            borderColor="border-secondaryBorderColor"
            text={t('cancel')}
            onClick={() => navigate('/')}
          />
      </AuthLayout>
    );
}

export default Reset;
